import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Newspaper, Settings, LogOut, Leaf, Menu, X, Building2, IdCard, MapPinned, ScanLine, FolderOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dasbor", icon: LayoutDashboard, end: true },
  { to: "/warga", label: "Data Warga", icon: Users },
  { to: "/kartu-keluarga", label: "Kartu Keluarga", icon: IdCard },
  { to: "/wilayah", label: "Wilayah Desa", icon: MapPinned },
  { to: "/scanner", label: "Smart Scanner", icon: ScanLine },
  { to: "/dokumen", label: "Dokumen Saya", icon: FolderOpen },
  { to: "/surat", label: "Surat Menyurat", icon: FileText },
  { to: "/berita", label: "Warta & Pengumuman", icon: Newspaper },
  { to: "/pengaturan", label: "Pengaturan Wilayah", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/village-profile").then((r) => {
      setProfile(r.data);
      if (!r.data) navigate("/setup");
    });
  }, [navigate]);

  if (profile === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="h-10 w-10 rounded-full border-4 border-[#064E3B] border-t-transparent animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#064E3B] text-white flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <div className="h-10 w-10 rounded-xl bg-[#D97706] grid place-items-center shrink-0">
            <Leaf size={22} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-lg leading-none">DigiDesa</div>
            <div className="text-xs text-emerald-200/70 mt-0.5">Administrasi Desa Digital</div>
          </div>
        </div>

        {/* Village badge */}
        <div className="p-4 mx-4 mt-4 rounded-xl bg-white/5 flex items-center gap-3">
          {profile?.regency_logo_url ? (
            <img src={profile.regency_logo_url} alt="logo" className="h-11 w-11 object-contain" />
          ) : (
            <div className="h-11 w-11 rounded-lg bg-white/10 grid place-items-center"><Building2 size={20} /></div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">Desa {profile?.village_name}</div>
            <div className="text-xs text-emerald-200/70 truncate">{profile?.regency_name}</div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 mt-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-white text-[#064E3B]" : "text-emerald-100/80 hover:bg-white/10"
                }`
              }
            >
              <item.icon size={19} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#D97706] grid place-items-center text-sm font-bold">
              {user?.name?.[0] || "A"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-emerald-200/60 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-emerald-100/80 hover:bg-white/10 transition-colors"
          >
            <LogOut size={18} /> Keluar
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 h-16 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu /></button>
          <span className="font-heading font-bold text-[#064E3B]">DigiDesa</span>
          <div className="w-6" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet context={{ profile }} />
        </main>
      </div>
    </div>
  );
}
